import { z, createValidationSchema } from '@tasama/utils';
import type { ServiceNeedsFormData } from '@tasama/types/domain';

/**
 * Service Needs form validation schema
 * Using zod and validation utilities from @tasama/utils
 */

type TFunction = (_key: string) => string;

const getValidationMessages = (t: TFunction) => ({
  selectedServices: {
    required: t('errors.validation.selectedServices.required'),
  },
  engagementTimeframe: {
    required: t('errors.validation.engagementTimeframe.required'),
  },
  budgetAllocated: {
    required: t('errors.validation.budgetAllocated.required'),
  },
  acceptTerms: {
    required: t('errors.validation.acceptTerms.required'),
  },
  establishmentTimeframe: {
    required: t('errors.validation.establishmentTimeframe.required'),
  },
});

const buildValidations = (t: TFunction) => {
  const messages = getValidationMessages(t);
  return {
    selectedServices: z.array(z.string()).min(1, {
      error: messages.selectedServices.required,
    }),
    engagementTimeframe: createValidationSchema.required(
      messages.engagementTimeframe.required
    ),
    budgetAllocated: createValidationSchema.required(
      messages.budgetAllocated.required
    ),
    acceptTerms: z.boolean().refine((value) => value === true, {
      error: messages.acceptTerms.required,
    }),
    establishmentTimeframe: createValidationSchema.required(
      messages.establishmentTimeframe.required
    ),
    goalsExpectations: z.string().optional().default(''),
  };
};

// Static fallback schemas (used where t is unavailable)
export const serviceNeedsValidations = buildValidations((key) => key);

// Service needs form schema
export const serviceNeedsSchema = z.object({
  selectedServices: serviceNeedsValidations.selectedServices,
  engagementTimeframe: serviceNeedsValidations.engagementTimeframe,
  budgetAllocated: serviceNeedsValidations.budgetAllocated,
  acceptTerms: serviceNeedsValidations.acceptTerms,
  establishmentTimeframe:
    serviceNeedsValidations.establishmentTimeframe.optional(),
  goalsExpectations: serviceNeedsValidations.goalsExpectations.optional(),
});

// International moving to KSA form schema
export const internationalServiceNeedsSchema = z.object({
  establishmentTimeframe: serviceNeedsValidations.establishmentTimeframe,
  goalsExpectations: serviceNeedsValidations.goalsExpectations,
  acceptTerms: serviceNeedsValidations.acceptTerms,
});

/**
 * Dynamic schema factory — selects the correct schema based on company type.
 * Replaces the 4-branch switch in the old store's validateForm method.
 *
 * Non-INTERNATIONAL schema: selectedServices is optional (per JIRA tickets),
 * only engagementTimeframe + budgetAllocated + acceptTerms are required.
 */
export const createServiceNeedsSchema = (
  companyType: string,
  _companyStatus?: string,
  t?: TFunction
) => {
  const v = t ? buildValidations(t) : serviceNeedsValidations;

  if (companyType === 'INTERNATIONAL') {
    return z.object({
      establishmentTimeframe: v.establishmentTimeframe,
      goalsExpectations: v.goalsExpectations,
      acceptTerms: v.acceptTerms,
    });
  }

  // KSA_PRIVATE, PIF_ESTABLISHED (pif-owned), PIF_UNDER_DEVELOPMENT, DEFAULT
  return z.object({
    selectedServices: z.array(z.string()).optional().default([]),
    engagementTimeframe: v.engagementTimeframe,
    budgetAllocated: v.budgetAllocated,
    acceptTerms: v.acceptTerms,
    establishmentTimeframe: z.string().optional().default(''),
    goalsExpectations: v.goalsExpectations,
  });
};

// Type exports (re-export for convenience)
export type { ServiceNeedsFormData } from '@tasama/types/domain';

// Field validation helper
export const validateServiceNeedsField = (
  field: keyof ServiceNeedsFormData,
  value: unknown
): string | null => {
  try {
    const fieldSchema =
      serviceNeedsValidations[field as keyof typeof serviceNeedsValidations];
    if (fieldSchema) {
      fieldSchema.parse(value);
      return null;
    }
    return null;
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.issues[0]?.message || 'Invalid value';
    }
    return 'Validation failed';
  }
};

// International field validation helper
export const validateInternationalServiceNeedsField = (
  field: 'establishmentTimeframe' | 'goalsExpectations' | 'acceptTerms',
  value: unknown
): string | null => {
  try {
    const fieldSchema = serviceNeedsValidations[field];
    if (fieldSchema) {
      fieldSchema.parse(value);
      return null;
    }
    return null;
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.issues[0]?.message || 'Invalid value';
    }
    return 'Validation failed';
  }
};

// Form validation helper
export const validateServiceNeedsForm = (
  data: unknown
): {
  success: boolean;
  errors?: Record<string, string>;
  data?: z.infer<typeof serviceNeedsSchema>;
} => {
  try {
    const result = serviceNeedsSchema.parse(data);
    return { success: true, data: result };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors: Record<string, string> = {};
      error.issues.forEach((err) => {
        if (err.path.length > 0) {
          errors[err.path.join('.')] = err.message;
        }
      });
      return { success: false, errors };
    }
    return { success: false, errors: { general: 'Validation failed' } };
  }
};

// International form validation helper
export const validateInternationalServiceNeedsForm = (
  data: unknown
): {
  success: boolean;
  errors?: Record<string, string>;
  data?: Pick<
    ServiceNeedsFormData,
    'establishmentTimeframe' | 'goalsExpectations' | 'acceptTerms'
  >;
} => {
  try {
    const result = internationalServiceNeedsSchema.parse(data);
    return { success: true, data: result };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors: Record<string, string> = {};
      error.issues.forEach((err) => {
        if (err.path.length > 0) {
          errors[err.path.join('.')] = err.message;
        }
      });
      return { success: false, errors };
    }
    return { success: false, errors: { general: 'Validation failed' } };
  }
};
